import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AccountBalanceWalletOutlined,
  Add,
  ArticleOutlined,
  AssessmentOutlined,
  AutoAwesomeOutlined,
  BarChartOutlined,
  ChevronRight,
  DeleteOutlined,
  DonutSmallOutlined,
  FolderOutlined,
  ShowChartOutlined,
  StoreOutlined,
  TableChartOutlined,
  WaterfallChartOutlined,
} from '@mui/icons-material';
import { MenuList, Paper, Popper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { orderBy } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { Company } from 'api';
import {
  breakpointAnalysisUrl,
  captableUrl,
  cashFlowLedgerUrl,
  companySummaryUrl,
  documentGenerationJobsUrl,
  documentsUrl,
  financialUrl,
  fundOwnershipUrl,
  valuationUrl,
  waterfallUrl,
} from 'common/config/urls';
import { DELETE_COMPANY_ENABLER, DOCUMENT_GENERATION_JOBS } from 'common/constants/features';
import { normalizePinnedItems, PINNED_COMPANIES_KEY, PinnedItemId } from 'common/constants/userPreferences';
import { useFirmScopedPermissions, useLocalStorageByUser } from 'common/hooks';
import { useStore } from 'common/store';
import { FirmCompany } from 'common/types/company';
import { UseStoreValues } from 'common/types/store';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import {
  PRIMARY_NAVIGATION_MENU_ZOOM_SCALE_VAR,
  PrimaryNavigationChip,
  PrimaryNavigationChipSkeleton,
  PrimaryNavigationMenu,
  PrimaryNavigationMenuAction,
  PrimaryNavigationMenuItem,
  PrimaryNavigationMenuItemLeadingAction,
  PrimaryNavigationMenuSearch,
  PrimaryNavigationMenuShowMoreItem,
} from 'components/ui';
import { useGlobalKeyboardShortcut, useSearchableLimitedOptions } from 'components/ui/hooks';
import { getCssPx, getPortalZoomScale } from 'components/ui/utilities/zoom';
import { OutlinedPushPin, PushPin } from 'icons';
import NewCompanyDialog from 'layouts/Main/components/NewCompanyDialog';
import { PRISM_NAVIGATION_PALETTE, PRISM_RADIUS } from 'theme/prismDesignTokens';
import { GUIDED_TOUR_TARGETS } from '../tours';
import { PRIMARY_NAV_INCLUDED_COMPANY_TYPES, PRIMARY_NAV_MENU_VISIBLE_ITEM_LIMIT } from './PrimaryNavigation.constants';
import { FIRM_PRIMARY_NAVIGATION_TEST_IDS } from './PrimaryNavigation.testIds';

interface CompanyChipProps {
  isLoading?: boolean;
  zoomLevel: number;
}

type SelectableCompany = FirmCompany & { isCurrent: boolean; isPinned: boolean };

const COMPANY_PAGE_LINKS = [
  { icon: <ArticleOutlined fontSize="inherit" />, title: 'Summary', urlBuilder: companySummaryUrl },
  { icon: <BarChartOutlined fontSize="inherit" />, title: 'Financials', urlBuilder: financialUrl },
  { icon: <TableChartOutlined fontSize="inherit" />, title: 'Cap Table', urlBuilder: captableUrl },
  { icon: <DonutSmallOutlined fontSize="inherit" />, title: 'Fund Ownership', urlBuilder: fundOwnershipUrl },
  { icon: <ShowChartOutlined fontSize="inherit" />, title: 'Breakpoints', urlBuilder: breakpointAnalysisUrl },
  { icon: <AccountBalanceWalletOutlined fontSize="inherit" />, title: 'Cash Flow', urlBuilder: cashFlowLedgerUrl },
  { icon: <AssessmentOutlined fontSize="inherit" />, title: 'Valuations', urlBuilder: valuationUrl },
  { icon: <WaterfallChartOutlined fontSize="inherit" />, title: 'Waterfall', urlBuilder: waterfallUrl },
  { icon: <FolderOutlined fontSize="inherit" />, title: 'Documents', urlBuilder: documentsUrl },
  {
    featureFlag: DOCUMENT_GENERATION_JOBS,
    icon: <AutoAwesomeOutlined fontSize="inherit" />,
    title: 'Document Generation Jobs',
    urlBuilder: documentGenerationJobsUrl,
  },
] as const;

const SUBMENU_CLOSE_DELAY_MS = 150;
// Sits above PrimaryNavigationMenu which renders as a MUI modal (z-index 1300).
const SUBMENU_Z_INDEX = 1301;

const getSubmenuPaperSx = (zoomLevel: number) => {
  const menuScale = getPortalZoomScale(zoomLevel);

  return {
    [PRIMARY_NAVIGATION_MENU_ZOOM_SCALE_VAR]: menuScale,
    bgcolor: PRISM_NAVIGATION_PALETTE.primary.menu.surface,
    borderRadius: getCssPx(PRISM_RADIUS.s * menuScale),
    boxShadow: `0 ${getCssPx(8 * menuScale)} ${getCssPx(24 * menuScale)} ${
      alpha(PRISM_NAVIGATION_PALETTE.primary.menu.shadow, 0.16)
    }`,
    minWidth: getCssPx(160 * menuScale),
    overflow: 'hidden',
    p: getCssPx(8 * menuScale),
  } as const;
};

const getEmptyCompanyMessage = ({
  activeCompany,
  companyCount,
}: {
  activeCompany: FirmCompany | null;
  companyCount: number;
}) => {
  if (activeCompany) {
    return 'No other companies found';
  }

  return companyCount === 0 ? 'No companies yet' : 'No companies found';
};

const isFirmNavigationCompany = (company: FirmCompany) =>
  !company.company_type || PRIMARY_NAV_INCLUDED_COMPANY_TYPES.has(company.company_type);

const CompanyChip = ({ isLoading = false, zoomLevel }: CompanyChipProps) => {
  const { firmSlugParam, companySlugParam } = useParams<{ firmSlugParam: string; companySlugParam: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [{ companyList, currentMeasurementDate }] = useStore() as UseStoreValues;
  const { canCreateCompany, canDeleteCompany } = useFirmScopedPermissions();
  const [, checkFeature] = useCheckFeatures();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState<HTMLElement | null>(null);
  const [activeSubmenuCompany, setActiveSubmenuCompany] = useState<SelectableCompany | null>(null);
  const [hoveredCompanyId, setHoveredCompanyId] = useState<number | null>(null);
  const [newCompanyDialogOpen, setNewCompanyDialogOpen] = useState(false);
  const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);
  const chipRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const submenuMenuListRef = useRef<HTMLUListElement>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submenuFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pinnedCompaniesRaw, setPinnedCompanies] = useLocalStorageByUser<PinnedItemId[]>(PINNED_COMPANIES_KEY, []);
  const pinnedCompanies = useMemo(() => normalizePinnedItems(pinnedCompaniesRaw), [pinnedCompaniesRaw]);

  const companies = useMemo(
    () => ((companyList as FirmCompany[] | null) ?? []).filter(isFirmNavigationCompany),
    [companyList]
  );

  const activeCompany = companySlugParam
    ? companies.find(company => company.company_slug === companySlugParam) ?? null
    : null;

  const selectableCompaniesWithPinState: SelectableCompany[] = useMemo(
    () =>
      companies.map(company => ({
        ...company,
        isCurrent: company.company_slug === companySlugParam,
        isPinned: pinnedCompanies.some(p => p.value === company.company_id),
      })),
    [companies, companySlugParam, pinnedCompanies]
  );

  const sortedSelectableCompanies = useMemo(
    () => orderBy(selectableCompaniesWithPinState, ['isPinned'], ['desc']),
    [selectableCompaniesWithPinState]
  );

  const {
    canShowFewerOptions,
    filteredOptions: filteredCompanies,
    hasMoreOptions,
    hiddenOptionCount: hiddenCompanyCount,
    resetSearchableOptions,
    searchValue,
    setSearchValue,
    setShowAllOptions,
    visibleOptions: visibleCompanies,
  } = useSearchableLimitedOptions({
    getSearchText: company => company.company_name,
    options: sortedSelectableCompanies,
    visibleLimit: PRIMARY_NAV_MENU_VISIBLE_ITEM_LIMIT,
  });

  const canOpenCompanyMenu = !isLoading && (companies.length > 0 || canCreateCompany);
  const canShowDeleteCompanyAction = canDeleteCompany
    && Boolean(activeCompany)
    && checkFeature(DELETE_COMPANY_ENABLER);
  const visibleCompanyPageLinks = useMemo(
    () => COMPANY_PAGE_LINKS.filter(link => !('featureFlag' in link) || checkFeature(link.featureFlag)),
    [checkFeature]
  );

  // --- Submenu timer helpers ---
  // Popper has no Modal/backdrop, so it never intercepts mouse events on the company
  // items below it. The timer lets the user move the cursor from item to flyout without
  // the submenu closing in between.

  const clearSubmenuCloseTimer = useCallback(() => {
    if (submenuCloseTimerRef.current !== null) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const clearSubmenuFocusTimer = useCallback(() => {
    if (submenuFocusTimerRef.current !== null) {
      clearTimeout(submenuFocusTimerRef.current);
      submenuFocusTimerRef.current = null;
    }
  }, []);

  // Clear pending submenu timers when the chip unmounts so delayed callbacks cannot mutate stale menu state.
  useEffect(
    () => () => {
      clearSubmenuCloseTimer();
      clearSubmenuFocusTimer();
    },
    [clearSubmenuCloseTimer, clearSubmenuFocusTimer]
  );

  const scheduleSubmenuClose = useCallback(() => {
    clearSubmenuCloseTimer();
    submenuCloseTimerRef.current = setTimeout(() => {
      setSubmenuAnchorEl(null);
      setActiveSubmenuCompany(null);
      setHoveredCompanyId(null);
    }, SUBMENU_CLOSE_DELAY_MS);
  }, [clearSubmenuCloseTimer]);

  const closeSubmenu = useCallback(() => {
    clearSubmenuCloseTimer();
    clearSubmenuFocusTimer();
    setSubmenuAnchorEl(null);
    setActiveSubmenuCompany(null);
    setHoveredCompanyId(null);
  }, [clearSubmenuCloseTimer, clearSubmenuFocusTimer]);

  const openSubmenu = useCallback(
    (el: HTMLElement, company: SelectableCompany) => {
      clearSubmenuCloseTimer();
      setSubmenuAnchorEl(el);
      setActiveSubmenuCompany(company);
      setHoveredCompanyId(company.company_id);
    },
    [clearSubmenuCloseTimer]
  );

  // --- Main menu handlers ---

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (canOpenCompanyMenu) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    closeSubmenu();
    resetSearchableOptions();
  }, [closeSubmenu, resetSearchableOptions]);

  const handleSelect = (company: SelectableCompany) => {
    handleClose();
    if (!firmSlugParam || !company.company_slug) {
      return;
    }
    const baseUrl = companySummaryUrl(firmSlugParam, company.company_slug);
    const dateParam = currentMeasurementDate?.date;
    navigate(dateParam ? `${baseUrl}?date=${dateParam}` : baseUrl);
  };

  const handleNavigateToPage = (urlBuilder: (firmSlug: string, companySlug: string) => string) => {
    if (!firmSlugParam || !activeSubmenuCompany?.company_slug) {
      return;
    }
    const baseUrl = urlBuilder(firmSlugParam, activeSubmenuCompany.company_slug);
    const dateParam = currentMeasurementDate?.date;
    handleClose();
    navigate(dateParam ? `${baseUrl}?date=${dateParam}` : baseUrl);
  };

  const handleAddCompany = () => {
    handleClose();
    setNewCompanyDialogOpen(true);
  };

  const handleTogglePin = useCallback(
    (event: React.SyntheticEvent, company: SelectableCompany) => {
      event.stopPropagation();
      if (company.isPinned) {
        setPinnedCompanies(pinnedCompanies.filter(p => p.value !== company.company_id));
      } else {
        setPinnedCompanies([...pinnedCompanies, { value: company.company_id }]);
      }
    },
    [pinnedCompanies, setPinnedCompanies]
  );

  // Keyboard: ArrowRight on a company item opens the submenu and focuses its first item.
  const handleCompanyKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>, company: SelectableCompany) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        openSubmenu(event.currentTarget, company);
        // setTimeout fires after React's synchronous commit, so the Popper is mounted and the ref is set.
        // Stored in a ref so it can be cancelled if the submenu closes before it fires.
        submenuFocusTimerRef.current = setTimeout(() => {
          submenuFocusTimerRef.current = null;
          submenuMenuListRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
        }, 0);
      }
    },
    [openSubmenu]
  );

  // Keyboard: ArrowLeft or Escape from the submenu returns focus to the company item.
  const handleSubmenuKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLUListElement>) => {
      if (event.key === 'ArrowLeft' || event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        const companyItem = submenuAnchorEl as HTMLElement | null;
        closeSubmenu();
        companyItem?.focus();
      }
    },
    [closeSubmenu, submenuAnchorEl]
  );

  const handleDeleteCompany = () => {
    handleClose();
    setEditCompanyDialogOpen(true);
  };

  const handleEditCompanyDialogClose = useCallback(
    (updatedCompany?: Company | null) => {
      setEditCompanyDialogOpen(false);

      if (!updatedCompany?.slug || !companySlugParam || updatedCompany.slug === companySlugParam) {
        return;
      }

      navigate(location.pathname.replace(`/companies/${companySlugParam}/`, `/companies/${updatedCompany.slug}/`), {
        replace: true,
      });
    },
    [companySlugParam, location.pathname, navigate]
  );

  const handleCompanyShortcut = useCallback((event: KeyboardEvent) => {
    event.preventDefault();
    if (chipRef.current) {
      setAnchorEl(chipRef.current);
    }
  }, []);

  // Keep the company selector reachable while shortcut ownership is still local to the primary nav.
  useGlobalKeyboardShortcut({
    enabled: canOpenCompanyMenu,
    key: 'k',
    metaOrCtrlKey: true,
    onKeyDown: handleCompanyShortcut,
  });

  return (
    <>
      <PrimaryNavigationChip
        ref={chipRef}
        icon={<StoreOutlined />}
        onClick={isLoading ? undefined : handleOpen}
        aria-busy={isLoading || undefined}
        aria-haspopup={isLoading ? undefined : 'listbox'}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.companyChip}
        data-tour={GUIDED_TOUR_TARGETS.companySelector}
        showDropdownIcon={!isLoading}
        value={isLoading ? (
          <PrimaryNavigationChipSkeleton zoomLevel={zoomLevel} />
        ) : activeCompany?.company_name ?? 'Companies'}
        zoomLevel={zoomLevel}
      />

      <PrimaryNavigationMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.companyMenu}
        disableAutoFocusItem
        disableEnforceFocus
        menuMinWidth={200}
        zoomLevel={zoomLevel}
        scrollHintItemLabelPlural="companies"
        scrollHintItemLabelSingular="company"
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
        MenuListProps={{ disableListWrap: true }}>
        <PrimaryNavigationMenuSearch
          clearAriaLabel="Clear company search"
          inputRef={searchInputRef}
          value={searchValue}
          onChange={event => setSearchValue(event.target.value)}
          onClear={() => setSearchValue('')}
          placeholder="Find a Company"
          fullWidth
        />
        {visibleCompanies.map(company => {
          const canSelectCompany = !company.isCurrent;
          const handleCompanyMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
            setHoveredCompanyId(company.company_id);
            openSubmenu(event.currentTarget, company);
          };
          const handleCompanyMouseLeave = () => {
            scheduleSubmenuClose();
          };

          return (
            <PrimaryNavigationMenuItem
              key={company.company_id}
              aria-current={company.isCurrent ? 'page' : undefined}
              leadingSlot={
                <PrimaryNavigationMenuItemLeadingAction
                  aria-label={company.isPinned ? 'Unpin company' : 'Pin company'}
                  isVisible={company.isPinned || hoveredCompanyId === company.company_id}
                  onClick={event => handleTogglePin(event, company)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleTogglePin(event, company);
                    }
                  }}>
                  {company.isPinned ? <PushPin fontSize="inherit" /> : <OutlinedPushPin fontSize="inherit" />}
                </PrimaryNavigationMenuItemLeadingAction>
              }
              endIcon={<ChevronRight />}
              isActive={company.isCurrent || activeSubmenuCompany?.company_id === company.company_id}
              onClick={canSelectCompany ? () => handleSelect(company) : undefined}
              onMouseEnter={handleCompanyMouseEnter}
              onMouseLeave={handleCompanyMouseLeave}
              onKeyDown={event => handleCompanyKeyDown(event, company)}
              data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.companyOption(
                company.company_slug ?? String(company.company_id)
              )}>
              {company.company_name}
            </PrimaryNavigationMenuItem>
          );
        })}
        {filteredCompanies.length === 0 && (
          <PrimaryNavigationMenuItem disabled tone="muted">
            {getEmptyCompanyMessage({ activeCompany, companyCount: companies.length })}
          </PrimaryNavigationMenuItem>
        )}
        {hasMoreOptions && (
          <PrimaryNavigationMenuShowMoreItem
            count={hiddenCompanyCount}
            itemLabelPlural="companies"
            itemLabelSingular="company"
            onClick={() => setShowAllOptions(true)}
            variant="more"
          />
        )}
        {canShowFewerOptions && (
          <PrimaryNavigationMenuShowMoreItem
            itemLabelPlural="companies"
            itemLabelSingular="company"
            onClick={() => setShowAllOptions(false)}
            variant="fewer"
          />
        )}
        {canCreateCompany && (
          <PrimaryNavigationMenuAction
            icon={<Add />}
            onClick={handleAddCompany}
            data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.addCompany}>
            Add New Company
          </PrimaryNavigationMenuAction>
        )}
        {canShowDeleteCompanyAction && (
          <PrimaryNavigationMenuAction
            icon={<DeleteOutlined />}
            onClick={handleDeleteCompany}
            tone="danger"
            data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.deleteCompany}>
            Delete Company
          </PrimaryNavigationMenuAction>
        )}
      </PrimaryNavigationMenu>

      {/*
        Popper has no Modal/backdrop, so it never intercepts mouse events on the company
        items — this is what caused the open/close cycle with Popover.
      */}
      <Popper
        anchorEl={submenuAnchorEl}
        open={Boolean(anchorEl) && Boolean(submenuAnchorEl)}
        placement="right-start"
        style={{ zIndex: SUBMENU_Z_INDEX }}>
        <Paper
          elevation={0}
          sx={getSubmenuPaperSx(zoomLevel)}
          onMouseEnter={clearSubmenuCloseTimer}
          onMouseLeave={scheduleSubmenuClose}>
          <MenuList
            dense
            disablePadding
            ref={submenuMenuListRef}
            onKeyDown={handleSubmenuKeyDown}>
            {visibleCompanyPageLinks.map(({ icon, title, urlBuilder }) => (
              <PrimaryNavigationMenuItem
                key={title}
                icon={icon}
                onClick={() => handleNavigateToPage(urlBuilder)}
                data-testid={FIRM_PRIMARY_NAVIGATION_TEST_IDS.companyPageLink(
                  activeSubmenuCompany?.company_slug ?? '',
                  title
                )}>
                {title}
              </PrimaryNavigationMenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>

      {newCompanyDialogOpen && (
        <NewCompanyDialog open={newCompanyDialogOpen} onClose={() => setNewCompanyDialogOpen(false)} />
      )}
      {editCompanyDialogOpen && (
        <NewCompanyDialog open={editCompanyDialogOpen} onClose={handleEditCompanyDialogClose} editMode />
      )}
    </>
  );
};

export default CompanyChip;
